import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

function AdminFacturen() {
  const { gebruiker } = useAuth();
  const navigate = useNavigate();
  const [facturen, setFacturen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('alle');
  const [genereerBezig, setGenereerBezig] = useState(false);
  const [actieBezig, setActieBezig] = useState(null);

  useEffect(() => {
    if (gebruiker?.rol !== 'super_admin' && gebruiker?.rol !== 'hoofdbeheerder') {
      navigate('/dashboard');
      return;
    }
    loadFacturen();
  }, [gebruiker, navigate]);

  const loadFacturen = async () => {
    try {
      const res = await api.get('/admin/facturen');
      setFacturen(res.data.facturen || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const genereerFacturen = async () => {
    if (!window.confirm('Genereer maandfacturen voor alle actieve bedrijven?')) return;
    setGenereerBezig(true);
    try {
      const res = await api.post('/admin/facturen/genereer');
      alert(res.data.message || 'Facturen gegenereerd');
      loadFacturen();
    } catch (e) {
      alert('Fout: ' + (e.response?.data?.message || e.message));
    } finally {
      setGenereerBezig(false);
    }
  };

  const downloadPdf = async (factuur) => {
    setActieBezig(`pdf-${factuur.id}`);
    try {
      const res = await api.get(`/admin/facturen/${factuur.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${factuur.factuurnummer}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('PDF fout: ' + e.message);
    } finally {
      setActieBezig(null);
    }
  };

  const verstuurEmail = async (factuur) => {
    if (!window.confirm(`Factuur ${factuur.factuurnummer} per email versturen naar ${factuur.bedrijf_email}?`)) return;
    setActieBezig(`email-${factuur.id}`);
    try {
      const res = await api.post(`/admin/facturen/${factuur.id}/email`);
      alert(res.data.message || 'Email verstuurd');
      loadFacturen();
    } catch (e) {
      alert('Email fout: ' + (e.response?.data?.message || e.message));
    } finally {
      setActieBezig(null);
    }
  };

  const wijzigStatus = async (factuur, nieuweStatus) => {
    setActieBezig(`status-${factuur.id}`);
    try {
      await api.put(`/admin/facturen/${factuur.id}/status`, { status: nieuweStatus });
      loadFacturen();
    } catch (e) {
      alert('Status fout: ' + e.message);
    } finally {
      setActieBezig(null);
    }
  };

  const filtered = facturen.filter(f => {
    if (filter === 'alle') return true;
    if (filter === 'open') return f.status === 'open';
    if (filter === 'betaald') return f.status === 'betaald';
    return true;
  });

  const formatEuro = (n) => '€' + Number(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatDatum = (d) => d ? new Date(d).toLocaleDateString('nl-NL') : '—';

  // Totalen
  const totaalOpen = facturen.filter(f => f.status === 'open').reduce((s, f) => s + parseFloat(f.totaal_bedrag || 0), 0);
  const totaalBetaald = facturen.filter(f => f.status === 'betaald').reduce((s, f) => s + parseFloat(f.totaal_bedrag || 0), 0);

  if (loading) return <div style={{ padding: 32 }}>Laden...</div>;

  const card = { background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
  const th = { padding: '12px 14px', textAlign: 'left', fontSize: 11, color: '#718096', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, borderBottom: '2px solid #e2e8f0' };
  const td = { padding: '14px', borderBottom: '1px solid #f1f5f9', fontSize: 14, verticalAlign: 'middle' };
  const btn = (color) => ({ padding: '6px 10px', fontSize: 12, border: 'none', borderRadius: 4, cursor: 'pointer', background: color, color: 'white', fontWeight: 600, marginRight: 4 });

  const filterBtn = (val, label) => (
    <button
      key={val}
      onClick={() => setFilter(val)}
      style={{
        padding: '8px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
        background: filter === val ? '#4a9eff' : '#e2e8f0',
        color: filter === val ? 'white' : '#475569',
        fontWeight: 600, fontSize: 13,
      }}
    >{label}</button>
  );

  const statusBadge = (status) => {
    const colors = { open: '#ed8936', betaald: '#48bb78', verzonden: '#4299e1', verlopen: '#e53e3e' };
    return (
      <span style={{
        background: colors[status] || '#a0aec0', color: 'white', padding: '3px 10px',
        borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5
      }}>{status}</span>
    );
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ marginTop: 0, marginBottom: 4 }}>Facturen</h1>
          <p style={{ color: '#718096', margin: 0 }}>Genereer, verstuur en beheer facturen voor je klanten.</p>
        </div>
        <button
          onClick={genereerFacturen}
          disabled={genereerBezig}
          style={{ padding: '10px 18px', background: '#48bb78', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
        >
          {genereerBezig ? 'Bezig...' : '+ Genereer maandfacturen'}
        </button>
      </div>

      {/* Totalen */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div style={{ ...card, padding: 20 }}>
          <div style={{ fontSize: 12, color: '#718096', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>Openstaand</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#ed8936', marginTop: 6 }}>{formatEuro(totaalOpen)}</div>
          <div style={{ fontSize: 13, color: '#718096' }}>{facturen.filter(f => f.status === 'open').length} facturen</div>
        </div>
        <div style={{ ...card, padding: 20 }}>
          <div style={{ fontSize: 12, color: '#718096', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>Betaald</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#48bb78', marginTop: 6 }}>{formatEuro(totaalBetaald)}</div>
          <div style={{ fontSize: 13, color: '#718096' }}>{facturen.filter(f => f.status === 'betaald').length} facturen</div>
        </div>
        <div style={{ ...card, padding: 20 }}>
          <div style={{ fontSize: 12, color: '#718096', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>Totaal</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#1a2332', marginTop: 6 }}>{facturen.length}</div>
          <div style={{ fontSize: 13, color: '#718096' }}>facturen ooit</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {filterBtn('alle', `Alle (${facturen.length})`)}
        {filterBtn('open', `Open (${facturen.filter(f => f.status === 'open').length})`)}
        {filterBtn('betaald', `Betaald (${facturen.filter(f => f.status === 'betaald').length})`)}
      </div>

      <div style={card}>
        {filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#718096' }}>
            <p style={{ fontSize: 16, marginTop: 0 }}>Nog geen facturen</p>
            <p style={{ fontSize: 14 }}>Klik op "Genereer maandfacturen" om voor alle actieve bedrijven facturen aan te maken.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Factuur</th>
                <th style={th}>Bedrijf</th>
                <th style={th}>Periode</th>
                <th style={th}>Bedrag</th>
                <th style={th}>Status</th>
                <th style={th}>Acties</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(f => (
                <tr key={f.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{f.factuurnummer}</div>
                    <div style={{ fontSize: 12, color: '#718096' }}>{formatDatum(f.created_at)}</div>
                  </td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{f.bedrijf_naam}</div>
                    <div style={{ fontSize: 12, color: '#718096' }}>{f.bedrijf_email}</div>
                  </td>
                  <td style={td}>
                    <div style={{ fontSize: 13 }}>{formatDatum(f.periode_start)}</div>
                    <div style={{ fontSize: 12, color: '#718096' }}>tot {formatDatum(f.periode_eind)}</div>
                  </td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{formatEuro(f.totaal_bedrag)}</div>
                    <div style={{ fontSize: 11, color: '#718096' }}>excl: {formatEuro(f.bedrag)} · btw: {formatEuro(f.btw_bedrag)}</div>
                  </td>
                  <td style={td}>
                    {statusBadge(f.status)}
                    {f.betaald_op && (
                      <div style={{ fontSize: 11, color: '#48bb78', marginTop: 4 }}>betaald: {formatDatum(f.betaald_op)}</div>
                    )}
                  </td>
                  <td style={td}>
                    <button onClick={() => downloadPdf(f)} disabled={actieBezig === `pdf-${f.id}`} style={btn('#4299e1')}>
                      {actieBezig === `pdf-${f.id}` ? '...' : 'PDF'}
                    </button>
                    <button onClick={() => verstuurEmail(f)} disabled={actieBezig === `email-${f.id}`} style={btn('#9f7aea')}>
                      {actieBezig === `email-${f.id}` ? '...' : 'Email'}
                    </button>
                    {f.status === 'open' && (
                      <button onClick={() => wijzigStatus(f, 'betaald')} disabled={actieBezig === `status-${f.id}`} style={btn('#48bb78')}>
                        ✓ Betaald
                      </button>
                    )}
                    {f.status === 'betaald' && (
                      <button onClick={() => wijzigStatus(f, 'open')} disabled={actieBezig === `status-${f.id}`} style={btn('#a0aec0')}>
                        ↶ Open
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 24, padding: 16, background: '#fef3c7', borderRadius: 8, fontSize: 13, color: '#78350f' }}>
        <strong>💡 Toekomst:</strong> Later koppelen we deze facturen automatisch aan Moneybird voor BTW-aangifte en bankkoppeling. De DB is daar al op voorbereid (extern_id, synced_at velden).
      </div>
    </>
  );
}

export default AdminFacturen;
